import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { getAnalyticsConsent, setAnalyticsConsent, type AnalyticsConsent } from '@/services/analytics';

interface CookieConsentContextValue {
  consent: AnalyticsConsent;
  showBanner: boolean;
  accept: () => void;
  reject: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  showPreferences: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<AnalyticsConsent>(() => getAnalyticsConsent());
  const [showBanner, setShowBanner] = useState(() => getAnalyticsConsent() === null);
  const [showPreferences, setShowPreferences] = useState(false);

  const accept = useCallback(() => {
    setAnalyticsConsent('accepted');
    setConsent('accepted');
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const reject = useCallback(() => {
    setAnalyticsConsent('rejected');
    setConsent('rejected');
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        accept,
        reject,
        openPreferences: () => setShowPreferences(true),
        closePreferences: () => setShowPreferences(false),
        showPreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
